/**
 * V108-6-5 — Today's shared Next step strip (design pass R2-2). One strip under the program cards:
 *
 *   status row  the step already moving, with its live state (state before role, AC-4):
 *                 failed  → the cause + its ONE fix link (failureCauseView, AC-5); for someone who can fix
 *                           it, that fix is the strip's filled primary over new work (AC-1)
 *                 waiting → "Check now" and "Open <vendor> again" as secondaries (AC-1)
 *                 a step in flight with someone else → "Remind" (copies a reminder; no fresh ask, AC-4)
 *   next row    the next new step, viewer-aware: someone who can do it gets the action
 *               ("Connect …", "Hand it to your coding agent"); someone who can't gets "Ask <owner> to …"
 * Copy says "You", never the viewer's own name. AC-2: an at-risk program's "Send <program> evidence asks".
 */
import { useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Button, Field, Select } from '@aigovops/design-system';
import { couldntCheckEntry, isCouldntCheckReason, vendorName } from '@aigovops/core';
import { patchEnabler, runConnectorCheck, type EnablerItem } from '../../lib/setup-api';
import { canDo, checkResult, isFixStep, personLabel, pickerPeople, type InFlight, type Person, type StripModel } from '../../lib/setup-model';
import { fixStepHref } from '../../lib/couldnt-check';
import { CouldntCheckChip } from '../CouldntCheck';
import { useQueryClient } from '@tanstack/react-query';
import { setupKeys } from './use-setup';
import { CONSOLE_URL } from './EnablerList';
import { ActorKind, ExtLink, State, UnlockChips } from './parts';
import { CiTrustAction, CiTrustState, ciTrustLine } from './CiTrust';
import { ClassifyActions } from './ClassifySystem';

export interface EvidenceAsk { program: string; label: string; count: number }

const VERB: Partial<Record<EnablerItem['actor'], string>> = { repo_agent: 'add', collector: 'install' };
const askVerb = (e: EnablerItem) => (e.actor === 'admin_console' ? e.title.replace(/^Connect /, 'connect ') : `${VERB[e.actor] ?? 'do'} ${e.title.replace(/^(Add|Install|Push|Send|Turn on) (the )?/i, '').toLowerCase()}`);
const when = (iso: string) => new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

export function NextStepStrip({ slug, prefix, model, people, viewer, canAssign, filled, evidenceAsks = [], onChanged, toast, announce }: {
  slug: string; prefix: string; model: StripModel; people: readonly Person[]; viewer: { user_id: string; role: string }; canAssign: boolean;
  /** Whether the strip holds the page's one filled primary (Today decides across surfaces). */
  filled: boolean; evidenceAsks?: readonly EvidenceAsk[];
  onChanged: () => Promise<unknown>; toast: (m: string, o?: { onUndo?: () => void }) => void; announce: (m: string) => void;
}) {
  const statusRef = useRef<HTMLDivElement>(null);
  const nextRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  /**
   * V108-8-19 (a11y review High): after Classify saves, the refresh can remove its row (or the whole strip). Focus goes
   * to the first target that still exists: the status row, the next row, the strip, else the page heading.
   */
  const focusAfterSave = () => window.setTimeout(() => {
    const t = [statusRef.current, nextRef.current?.querySelector<HTMLElement>('[data-part="next"]') ?? null, sectionRef.current, document.querySelector<HTMLElement>('main h1, h1')]
      .find((x): x is HTMLElement => !!x && x.isConnected);
    if (!t) return;
    if (!t.hasAttribute('tabindex')) t.setAttribute('tabindex', '-1');
    t.focus();
  }, 0);
  const lookupClient = useQueryClient();
  if (!model.status && !model.next && !evidenceAsks.length) return null;
  const st = model.status;
  const stepHref = (e: EnablerItem) => `${prefix}/setup?step=${encodeURIComponent(e.id)}#steps`;
  const remind = async (e: EnablerItem, owner: string | null) => {
    const name = personLabel(owner, viewer.user_id, people) ?? 'them';
    const url = new URL(stepHref(e), window.location.origin).toString();
    const text = `Reminder: "${e.title}" in GAC Pro is waiting on you. ${url}`;
    try { await navigator.clipboard.writeText(text); toast(`Copied a reminder for ${name}. Paste it in chat or email.`); }
    catch { toast(`Couldn't copy. Send ${name} this link: ${url}`); }
  };

  const lookup = lookupClient;
  const statusRow = (st: InFlight, i: number): ReactNode => {
    const e = st.step;
    const owner = personLabel(st.owner, viewer.user_id, people);
    const fixer = i === 0 && model.primary === 'status';
    const mine = st.owner === viewer.user_id;
    const canAct = model.approver || (mine && canDo({ role: viewer.role }, e));
    const checkNow = async (id: string) => {
      try {
        await runConnectorCheck(slug, id);
        await onChanged();
        const fresh = lookup.getQueryData<{ enablers: EnablerItem[] }>(setupKeys.enablers(slug))?.enablers.find((x) => x.id === e.id);
        announce(checkResult(fresh, slug));
      } catch (err) { announce(err instanceof Error ? err.message : 'The check could not start.'); }
    };
    let line: ReactNode; let acts: ReactNode = null;
    if (st.kind === 'trust' && e.progress.ci_trust) {
      // V108-8-13: the CI gate's runs arrive but don't count yet. Trust it (owner / admin), else Ask an admin.
      // Glance review (High): what trusting is worth, the same unlock chip the next row uses.
      line = <><CiTrustState t={e.progress.ci_trust} /><span><strong>{e.title}</strong> · {ciTrustLine(e.progress.ci_trust)}</span><UnlockChips step={e} /></>;
      acts = <CiTrustAction slug={slug} step={e} approver={model.approver} primary={fixer && filled} viewerId={viewer.user_id} onChanged={onChanged} announce={announce} compact />;
    } else if (isFixStep(e) && (st.kind === 'failed' || st.kind === 'fix')) {
      // V109-1-19 (H3/H4): a "Couldn't check" fix — named for its source, the same words and the same
      // destination (the Setup step for that source) as readiness and the control page; it leads the strip.
      // R3 (R2-H6): the same grey pill as readiness and Setup; the shared reason words first, the provider's
      // cause only when no reason is known. A value or evidence fix opens where it is done (R2-H2/H3).
      const words = [...new Set((e.progress.fix?.reasons ?? []).filter(isCouldntCheckReason).map((r) => couldntCheckEntry(r).words))];
      const why = words.length ? words.join(' · ') : st.cause?.line ?? 'GAC Pro couldn\'t check some results';
      line = <><CouldntCheckChip /><span><strong>{e.title}</strong> · {why}{!canAct && owner ? ` · ${owner} has the fix` : ''}</span></>;
      const href = fixStepHref(prefix, e);
      acts = canAct
        ? <Link className="su-btn" data-variant={fixer && filled ? 'primary' : undefined} to={href} data-part="strip-fix">{e.title}</Link>
        : owner ? <Button compact onClick={() => void remind(e, st.owner)}>{`Remind ${owner}`}</Button>
          : <Button compact onClick={() => void navigator.clipboard?.writeText(new URL(href, window.location.origin).toString()).then(() => toast('Link copied. Send it to an owner or admin.'), () => toast('Open Setup to see the step.'))}>Copy a link for an admin</Button>;
    } else if (st.kind === 'failed' && st.cause) {
      line = <><State tone="attention" glyph="alert">Needs attention</State><span><strong>{e.title}</strong> · {st.cause.line}{!canAct && owner ? ` · ${owner} has the fix` : ''}</span></>;
      acts = canAct
        ? (st.cause.action.external ? <ExtLink href={st.cause.action.href} variant={fixer && filled ? 'primary' : undefined}>{st.cause.action.label}</ExtLink>
          : <Link className="su-btn" data-variant={fixer && filled ? 'primary' : undefined} to={st.cause.action.href}>{st.cause.action.label}</Link>)
        : owner ? <Button compact onClick={() => void remind(e, st.owner)}>{`Remind ${owner}`}</Button>
          : <Button compact onClick={() => void navigator.clipboard?.writeText(new URL(stepHref(e), window.location.origin).toString()).then(() => toast('Link copied. Send it to an owner or admin.'), () => toast('Open Setup to see the step.'))}>Copy a link for an admin</Button>;
    } else if (st.kind === 'waiting') {
      const vendor = vendorName(e.progress.kind ?? '');
      line = <><State tone="progress" glyph="clock">Waiting for our first read</State><span><strong>{e.title}</strong>{e.progress.consent_started_at ? ` · ${owner && owner !== 'You' ? owner : 'You'} opened ${vendor} ${when(e.progress.consent_started_at)}` : ''}</span></>;
      const url = e.progress.kind ? CONSOLE_URL[e.progress.kind] : undefined;
      acts = canAct ? <>
        {e.progress.connector_id && <Button compact onClick={() => void checkNow(e.progress.connector_id!)}>Check now<span className="su-sr"> for {e.title}</span></Button>}
        {url && <ExtLink href={url} variant="ghost">Open {vendor} again</ExtLink>}
      </> : <Button compact onClick={() => void remind(e, st.owner)}>{`Remind ${owner ?? 'the owner'}`}</Button>;
    } else if (st.kind === 'partial' && e.progress.accounts) {
      const a = e.progress.accounts;
      line = <><State tone="partial" glyph="clock">{a.read.length} of {a.read.length + a.unread.length} accounts read</State><span><strong>{e.title}</strong> · nothing counts until every production account is read</span></>;
      acts = <Link className="su-btn" to={stepHref(e)}>Add the other {a.unread.length}</Link>;
    } else {
      line = <span><strong>{e.title}</strong> · {mine ? 'sent to you' : `asked ${owner ?? 'a teammate'}`}{e.assigned_at ? ` ${when(e.assigned_at)}` : ''}</span>;
      acts = mine ? <Link className="su-btn" to={stepHref(e)}>Open the step</Link>
        : <><Button compact onClick={() => void remind(e, st.owner)}>{`Remind ${owner ?? 'them'}`}</Button>
          {canAssign && <Button variant="ghost" compact onClick={() => {
            const prev = e.assignee_user_id;
            void patchEnabler(slug, e.id, { assignee_user_id: null }).then(onChanged).then(() => {
              toast(`${e.title} is no longer sent to ${owner ?? 'them'}.`, { onUndo: () => { void patchEnabler(slug, e.id, { assignee_user_id: prev }).then(onChanged); } });
              window.setTimeout(() => (nextRef.current?.querySelector<HTMLElement>('select, button, a') ?? statusRef.current)?.focus(), 0);
            });
          }}>Change who<span className="su-sr"> for {e.title}</span></Button>}</>;
    }
    return <div key={e.id} className="su-strip-row" data-part={i === 0 ? 'status' : 'status-more'} ref={i === 0 ? statusRef : undefined} tabIndex={-1}><p className="su-st-line">{line}</p><div className="su-strip-act">{acts}</div></div>;
  };
  const status = st ? [st, ...model.others.slice(0, 2)].map(statusRow) : null;
  const more = model.others.length - 2;

  return (
    <section className="su-strip" aria-label="Next step" data-part="next-step-strip" ref={sectionRef} tabIndex={-1}>
      {status}
      {more > 0 && <div className="su-strip-row" data-part="status-more"><p className="su-st-line"><Link to={`${prefix}/setup#steps`}>{more} more {more === 1 ? 'step needs' : 'steps need'} attention in Setup</Link></p></div>}
      {model.next && <div ref={nextRef}>{model.next.progress.gap?.kind === 'system_classify'
        ? <ClassifyNextRow key={model.next.id} slug={slug} e={model.next} approver={model.approver} filled={filled && model.primary === 'next'} onChanged={onChanged} announce={announce}
          focusStatus={focusAfterSave} />
        : <NextRow key={model.next.id} slug={slug} e={model.next} people={people} viewer={viewer} approver={model.approver} canAssign={canAssign} filled={filled && model.primary === 'next'}
        href={stepHref(model.next)} prefix={prefix} onChanged={onChanged} toast={toast} focusStatus={() => window.setTimeout(() => statusRef.current?.focus(), 0)} />}</div>}
      {evidenceAsks.length > 0 && <div className="su-strip-row" data-part="evidence-asks"><p className="su-st-line"><span>Evidence only a person can add</span></p>
        <div className="su-strip-act">{evidenceAsks.map((a) => <Link key={a.program} className="su-btn" to={`${prefix}/setup?program=${a.program}#nosrc-${a.program}`}>{`Send ${a.label} evidence asks · ${a.count} not asked`}</Link>)}</div></div>}
    </section>
  );
}

/**
 * V108-8-19: "Classify <system>" as Today's next step. One row like the others (glance review): status + effort on the
 * left, the one action on the right; the questions open in a drawer, so the strip never grows into a form.
 */
function ClassifyNextRow({ slug, e, approver, filled, onChanged, announce, focusStatus }: {
  slug: string; e: EnablerItem; approver: boolean; filled: boolean; onChanged: () => Promise<unknown>; announce: (m: string) => void; focusStatus: () => void;
}) {
  const gap = e.progress.gap as Extract<NonNullable<EnablerItem['progress']['gap']>, { kind: 'system_classify' }>;
  return (
    <div className="su-strip-row" data-part="next" tabIndex={-1}>
      <div>
        <span className="su-eyebrow">Next step</span>
        <h3 className="su-strip-title">{e.title}</h3>
        <p className="su-strip-meta"><State tone="attention" glyph="alert">No AI-duty checks yet</State><span className="su-kind">{`In GAC Pro · ${e.effort_min} min`}</span></p>
      </div>
      <div className="su-strip-act"><ClassifyActions gap={gap} slug={slug} approver={approver} primary={filled} mode="drawer" showWhy={false}
        onChanged={onChanged} announce={announce} afterSave={focusStatus} /></div>
    </div>
  );
}

function NextRow({ slug, e, people, viewer, approver, canAssign, filled, href, prefix, onChanged, toast, focusStatus }: {
  slug: string; e: EnablerItem; people: readonly Person[]; viewer: { user_id: string; role: string }; approver: boolean; canAssign: boolean; filled: boolean;
  href: string; prefix: string; onChanged: () => Promise<unknown>; toast: (m: string, o?: { onUndo?: () => void }) => void; focusStatus: () => void;
}) {
  const doable = canDo({ role: approver ? 'owner' : viewer.role }, e);
  const options = pickerPeople(e, people, viewer.user_id);
  const [who, setWho] = useState(options[0]?.user_id ?? '');
  const v = filled ? 'primary' : undefined;
  let act: ReactNode;
  if (doable) {
    act = e.actor === 'repo_agent' ? <Link className="su-btn" data-variant={v} to={`${prefix}/setup?agent=open#agent`}>Hand it to your coding agent</Link>
      : <Link className="su-btn" data-variant={v} to={href}>{e.actor === 'admin_console' ? e.title.replace(/ production$/, '') : 'Get the command'}</Link>;
  } else if (canAssign && options.length) {
    act = <>
      <Field id={`strip-ask-${e.id}`} label="Ask"><Select value={who} onChange={(ev) => setWho(ev.target.value)}>
        {options.map((o) => <option key={o.user_id} value={o.user_id}>{o.name} · {o.role}</option>)}
      </Select></Field>
      <Button variant={filled ? 'primary' : 'secondary'} onClick={() => void patchEnabler(slug, e.id, { assignee_user_id: who }).then(onChanged).then(() => {
        const name = options.find((o) => o.user_id === who)?.name ?? 'them';
        toast(`Asked ${name} to ${askVerb(e)}. It shows on their Today.`, { onUndo: () => { void patchEnabler(slug, e.id, { assignee_user_id: null }).then(onChanged); } });
        focusStatus();
      })}>{`Ask someone to ${askVerb(e)}`}</Button>
    </>;
  } else {
    act = <Button variant={filled ? 'primary' : 'secondary'} onClick={() => void navigator.clipboard?.writeText(new URL(href, window.location.origin).toString()).then(() => toast('Link copied. Send it to an owner or admin.'), () => toast(`Send an owner or admin this link: ${new URL(href, window.location.origin).toString()}`))}>Copy a link for an admin</Button>;
  }
  return (
    <div className="su-strip-row" data-part="next">
      <div>
        <span className="su-eyebrow">Next step</span>
        <h3 className="su-strip-title">{e.title}</h3>
        <p className="su-strip-meta"><UnlockChips step={e} /><ActorKind step={e} /></p>
      </div>
      <div className="su-strip-act">{act}</div>
    </div>
  );
}
